import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { AdapterStepConfig, TourAdapter, createDriverJsAdapter } from "./adapter";
import { TUTORIAL_STEPS } from "./steps";
import {
  TutorialEventHandler,
  TutorialEventName,
  TutorialStatus,
  TutorialStepDef,
} from "./types";
import { useIsMobile } from "./useIsMobile";

export interface UseTutorialOptions {
  steps?: TutorialStepDef[];
  adapter?: TourAdapter;
  onEvent?: TutorialEventHandler;
  translate?: (key: string, fallback?: string) => string;
  disableOnMobile?: boolean;
  navigationDelayMs?: number;
  // A-5 (#125): current user roles. Steps with requiredRole are filtered out
  // (and a step_skipped_by_role event is emitted per filtered step) before the
  // tour starts. When absent, no role filtering occurs.
  currentRoles?: string[];
}

export interface UseTutorialApi {
  status: TutorialStatus;
  activeStepIndex: number | null;
  totalSteps: number;
  start(): void;
  skip(): void;
  isMobileAndDisabled: boolean;
}

const noopEvent: TutorialEventHandler = () => undefined;
const defaultTranslate = (_key: string, fallback?: string) => fallback ?? "";

const NAVIGATION_DELAY_DEFAULT_MS = 250;

export function useTutorial(options: UseTutorialOptions = {}): UseTutorialApi {
  const {
    steps: rawSteps = TUTORIAL_STEPS,
    adapter: providedAdapter,
    onEvent = noopEvent,
    translate = defaultTranslate,
    disableOnMobile = true,
    navigationDelayMs = NAVIGATION_DELAY_DEFAULT_MS,
    currentRoles,
  } = options;

  // Filter out role-gated steps the user can't reach, emitting a per-step
  // skip event so downstream analytics (W5 GA4) can measure role-based drop-off.
  const steps = useMemo(() => {
    if (!currentRoles || currentRoles.length === 0) return rawSteps;
    const filtered: TutorialStepDef[] = [];
    for (const step of rawSteps) {
      if (step.requiredRole && !currentRoles.includes(step.requiredRole)) {
        onEvent({
          event: "step_skipped_by_role",
          stepId: step.id,
          totalSteps: rawSteps.length,
          timestamp: Date.now(),
          requiredRole: step.requiredRole,
          currentRoles,
        });
        continue;
      }
      filtered.push(step);
    }
    return filtered;
    // onEvent is captured intentionally; changes to it after mount won't
    // retroactively re-fire skip events on the same session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSteps, currentRoles]);

  const history = useHistory();
  const isMobile = useIsMobile();
  const isMobileAndDisabled = disableOnMobile && isMobile;

  const [status, setStatus] = useState<TutorialStatus>("idle");
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null);

  const adapterRef = useRef<TourAdapter | null>(null);
  const stepsRef = useRef(steps);
  const translateRef = useRef(translate);
  const onEventRef = useRef(onEvent);
  const activeStepIndexRef = useRef<number | null>(null);
  const totalStepsRef = useRef(steps.length);

  useEffect(() => {
    stepsRef.current = steps;
    translateRef.current = translate;
    onEventRef.current = onEvent;
    totalStepsRef.current = steps.length;
  }, [steps, translate, onEvent]);

  const emit = useCallback((event: TutorialEventName, stepIndex?: number) => {
    const stepId =
      stepIndex != null ? stepsRef.current[stepIndex]?.id : undefined;
    onEventRef.current({
      event,
      stepId,
      stepIndex,
      totalSteps: totalStepsRef.current,
      timestamp: Date.now(),
    });
  }, []);

  const buildAdapterSteps = useCallback((): AdapterStepConfig[] => {
    return stepsRef.current.map((s) => ({
      targetSelector: s.targetSelector,
      title: translateRef.current(s.titleKey, s.fallbackTitle ?? s.id),
      description: translateRef.current(
        s.descriptionKey,
        s.fallbackDescription ?? ""
      ),
      side: s.side,
    }));
  }, []);

  const navigateToStep = useCallback(
    (index: number) => {
      const step = stepsRef.current[index];
      if (!step) return;
      if (history.location.pathname !== step.route) {
        history.push(step.route);
      }
    },
    [history]
  );

  const setActiveIndex = useCallback((index: number | null) => {
    activeStepIndexRef.current = index;
    setActiveStepIndex(index);
  }, []);

  const start = useCallback(() => {
    if (isMobileAndDisabled) {
      // Safety: mobile viewports skip the tour entirely; W3/W5 can surface a
      // simplified walkthrough page if the operator continues from mobile.
      return;
    }
    if (status === "running") return;

    const adapter = providedAdapter ?? createDriverJsAdapter();
    adapterRef.current = adapter;

    setStatus("running");
    setActiveIndex(0);
    emit("tutorial_start", 0);
    navigateToStep(0);

    // Give the target route a tick to mount so driver.js can find the anchor.
    // Without this, spotlight misses on first render and driver.js silently
    // skips the popover.
    window.setTimeout(() => {
      adapter.start(buildAdapterSteps(), {
        onStepActive: (idx) => {
          const previous = activeStepIndexRef.current;
          setActiveIndex(idx);
          navigateToStep(idx);
          if (previous != null && idx > previous) {
            emit("step_complete", previous);
          }
        },
        onFinish: () => {
          const lastIdx = totalStepsRef.current - 1;
          emit("step_complete", lastIdx);
          emit("tutorial_finish");
          setStatus("finished");
          setActiveIndex(null);
        },
        onSkip: () => {
          emit("tutorial_skip", activeStepIndexRef.current ?? undefined);
          setStatus("skipped");
          setActiveIndex(null);
        },
      });
    }, navigationDelayMs);
  }, [
    isMobileAndDisabled,
    status,
    providedAdapter,
    emit,
    navigateToStep,
    buildAdapterSteps,
    setActiveIndex,
    navigationDelayMs,
  ]);

  const skip = useCallback(() => {
    if (status !== "running") return;
    adapterRef.current?.destroy();
    // adapter's onDestroyStarted triggers onSkip → sets state + emits event.
  }, [status]);

  useEffect(() => {
    return () => {
      adapterRef.current?.destroy();
      adapterRef.current = null;
    };
  }, []);

  return useMemo(
    () => ({
      status,
      activeStepIndex,
      totalSteps: steps.length,
      start,
      skip,
      isMobileAndDisabled,
    }),
    [status, activeStepIndex, steps.length, start, skip, isMobileAndDisabled]
  );
}
